// eslint-disable-next-line import/no-extraneous-dependencies
import qs from 'qs';
import { getShopperId } from '../util/auth';
import {
  trackAnonymousCustomerFeedbackSubmissionAttempted,
  trackAnonymousCustomerFeedbackSubmissionFailed,
  trackAnonymousCustomerFeedbackSubmissionSuccess,
  trackCancellationAttempted,
  trackCancelRequestFailed,
  trackCancelRequestSuccess,
  trackCustomerFeedbackSubmissionAttempted,
  trackCustomerFeedbackSubmissionFailed,
  trackCustomerFeedbackSubmissionSuccess,
  trackShippingAddressChangeAttempted,
  trackShippingAddressChangeRequestFailed,
  trackShippingAddressChangeRequestSuccess,
} from './newRelic/orderUpdateNewRelicTrackingApi';

import { dispatchGetRequest, dispatchPostRequest } from '../util/apiUtils';
import CancelRequestError from './apiTypes/CancelRequestError';
import FeedbackGroups, {
  FeedbackGroup,
  FeedbackGroupState,
} from '../types/feedback/FeedbackGroup';
import { FeedbackType } from '../types/feedback/Feedback';
import { FeedbackSubmission } from '../types/feedback/FeedbackResponse';

const prodOrcaBaseUrl = process.env.PRODUCTION_ORCA_BASE_URL || '';
const stagingOrcaBaseUrl = process.env.STAGING_ORCA_BASE_URL || '';

/**
 * Gets the base url for ORCA service
 * In the future, consider adding timeouts as part of the configuration.
 */
const getConfigForEnvironment = (environment: string): string => {
  if (!environment) {
    console.warn(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return stagingOrcaBaseUrl;
  }
  switch (environment) {
    case 'PRODUCTION':
      return prodOrcaBaseUrl;
    case 'STAGING':
      return stagingOrcaBaseUrl;
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return stagingOrcaBaseUrl;
  }
};

/**
 * Invoke the cancellation service to request cancellation for this set of lineItem(s)
 */
type CancelItemUsingCancellationServiceParams = {
  tenantEnvironment: string;
  orderNumber: string;
  itemIds: string[];
  vistacartTenantName: string;
  cancellationSourcePage: string;
};

export const cancelItemUsingCancellationService = async ({
  tenantEnvironment,
  orderNumber,
  itemIds,
  vistacartTenantName,
  cancellationSourcePage,
}: CancelItemUsingCancellationServiceParams) => {
  const baseUrl = getConfigForEnvironment(tenantEnvironment);
  const shopperId = getShopperId();
  const params = {
    reason: 'Customer cancellation from Order Details.',
    requester: 'OrderDetailsUI',
  };
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/cancel`;

  const metricsAdditionalData = {
    orderId: orderNumber,
    cartTenant: vistacartTenantName,
    page: cancellationSourcePage,
    itemIds,
  };

  // notify NR for tracking/monitoring
  trackCancellationAttempted(shopperId, metricsAdditionalData);

  try {
    const response = await dispatchPostRequest({ url, body: itemIds, params });
    const { itemResults } = await response.json();
    const successes: any[] = [];
    const failures: any[] = [];
    if (itemResults) {
      itemResults.forEach((item: { requestSucceeded: boolean }) => {
        if (item.requestSucceeded === true) {
          successes.push(item);
        } else {
          failures.push(item);
        }
      });
    }

    const metricsDataWithSuccessAndFailure = {
      ...metricsAdditionalData,
      successes,
      failures,
    };
    trackCancelRequestSuccess(shopperId, metricsDataWithSuccessAndFailure);
    if (response.status === 200) {
      // TODO: for now, since we are only allow single item cancel,
      // this is fine once we start supporting multi-item cancels, we will want to inspect each itemResult here
      if (successes.length <= 0) {
        // report that none of the items were successful
        throw new CancelRequestError(
          'no items were successfully cancelled',
          false,
          failures
        );
      }
    } else if (response.status >= 500) {
      throw new CancelRequestError(
        'request to cancel failed due to transient error, please retry.',
        true
      );
    } else {
      throw new CancelRequestError(
        'request to cancel failed due to invalid request',
        false
      );
    }
  } catch (err) {
    // should we pass successMetricsData here?
    trackCancelRequestFailed(shopperId, err as object, metricsAdditionalData);
    throw err;
  }
};

/**
 * Invoke ORCA to request a shipping address change for the specified order
 */
type RequestShippingAddressChangeParams = {
  tenantEnvironment: string;
  orderNumber: string;
  addressToUpdate: object;
  vistacartTenantName: string;
};

export const requestShippingAddressChange = async ({
  tenantEnvironment,
  orderNumber,
  addressToUpdate,
  vistacartTenantName,
}: RequestShippingAddressChangeParams) => {
  const baseUrl = getConfigForEnvironment(tenantEnvironment);
  const shopperId = getShopperId();
  const metricsAdditionalData = {
    orderId: orderNumber,
    cartTenant: vistacartTenantName,
  };
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/shippingAddressChangeRequest`;
  // notify NR for tracking/monitoring
  trackShippingAddressChangeAttempted(shopperId, metricsAdditionalData);

  try {
    await dispatchPostRequest({ url, body: addressToUpdate });
    trackShippingAddressChangeRequestSuccess(shopperId, metricsAdditionalData);
  } catch (err) {
    trackShippingAddressChangeRequestFailed(
      shopperId,
      err as object,
      metricsAdditionalData
    );
    // TODO: come back and type out next time working on this endpoint
    const error = err as any;
    if (error.response?.status === 400) {
      // invalid request, this is important to pass back into the component,
      // so it can tell the customer it was invalid!
      error.failureType = 'INVALID';
    }

    throw err;
  }
};

/**
 * Fetch the feedback groups for the given order
 */
type GetFeedbackGroupsForOrderParams = {
  orderNumber: string;
  shopperId: string;
  states: FeedbackGroupState[];
  types: FeedbackType[];
  environment: string;
};

export const getFeedbackGroupsForOrder = async ({
  orderNumber,
  shopperId,
  states,
  types,
  environment,
}: GetFeedbackGroupsForOrderParams): Promise<FeedbackGroup[]> => {
  const baseUrl = getConfigForEnvironment(environment);
  const searchParams = qs.stringify(
    { states, types },
    { arrayFormat: 'repeat' }
  );
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/feedbackGroups?${searchParams}`;

  // TODO: add try catch and track fetching error?
  const response = await dispatchGetRequest({ url });
  const feedbackGroups = (await response.json()) as FeedbackGroups;
  return feedbackGroups.feedbackGroups;
};

/**
 * Fetch the feedback groups for the given anonymous order
 */
type GetFeedbackGroupsForAnonymousOrderParams = {
  orderNumber: string;
  email: string;
  states: FeedbackGroupState[];
  types: FeedbackType[];
  environment: string;
};

export const getFeedbackGroupsForAnonymousOrder = async ({
  orderNumber,
  email,
  states,
  types,
  environment,
}: GetFeedbackGroupsForAnonymousOrderParams): Promise<FeedbackGroup[]> => {
  const baseUrl = getConfigForEnvironment(environment);
  const searchParams = qs.stringify(
    { states, types },
    { arrayFormat: 'repeat' }
  );
  const url = `${baseUrl}/api/v1/email/${email}/orders/${orderNumber}/feedbackGroups?${searchParams}`;

  // TODO: add try catch and track fetching error?
  const response = await dispatchGetRequest({ url });
  const feedbackGroups = (await response.json()) as FeedbackGroups;
  return feedbackGroups.feedbackGroups;
};

/**
 * Fetch the feedback group by GroupId for the given order
 */
type GetFeedbackGroupForOrderParams = {
  orderNumber: string;
  shopperId: string;
  groupId: string;
  environment: string;
  states: FeedbackGroupState[];
};

export const getFeedbackGroupForOrder = async ({
  orderNumber,
  shopperId,
  groupId,
  environment,
  states,
}: GetFeedbackGroupForOrderParams): Promise<FeedbackGroup> => {
  const baseUrl = getConfigForEnvironment(environment);
  const searchParams = qs.stringify({ states }, { arrayFormat: 'repeat' });
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/groupId/${groupId}/feedbackGroup?${searchParams}`;

  // TODO: add try catch and track fetching error?
  const response = await dispatchGetRequest({ url });
  const feedbackGroup = (await response.json()) as FeedbackGroup;
  return feedbackGroup;
};

/**
 * Fetch the feedback group by GroupId for the given anonymous order
 */
type GetFeedbackGroupForAnonymousOrderParams = {
  orderNumber: string;
  email: string;
  groupId: string;
  environment: string;
  states: FeedbackGroupState[];
};

export const getFeedbackGroupForAnonymousOrder = async ({
  orderNumber,
  email,
  groupId,
  environment,
  states,
}: GetFeedbackGroupForAnonymousOrderParams): Promise<FeedbackGroup> => {
  const baseUrl = getConfigForEnvironment(environment);
  const searchParams = qs.stringify({ states }, { arrayFormat: 'repeat' });
  const url = `${baseUrl}/api/v1/email/${email}/orders/${orderNumber}/groupId/${groupId}/feedbackGroup?${searchParams}`;

  // TODO: add try catch and track fetching error?
  const response = await dispatchGetRequest({ url });
  const feedbackGroup = (await response.json()) as FeedbackGroup;
  return feedbackGroup;
};

/**
 * Submit the customer's feedback to a design review request group
 */
type SubmitCustomerFeedbackGroupParams = {
  orderNumber: string;
  groupId: string;
  shopperId: string;
  feedbackSubmission: FeedbackSubmission;
  environment: string;
  trackingData: object;
};

export const submitCustomerFeedbackGroup = async ({
  orderNumber,
  groupId,
  shopperId,
  feedbackSubmission,
  environment,
  trackingData,
}: SubmitCustomerFeedbackGroupParams) => {
  const baseUrl = getConfigForEnvironment(environment);
  const url = `${baseUrl}/api/v1/shoppers/${shopperId}/orders/${orderNumber}/groupId/${groupId}/submitFeedback`;

  trackCustomerFeedbackSubmissionAttempted(shopperId, trackingData);

  try {
    await dispatchPostRequest({ url, body: feedbackSubmission });
    trackCustomerFeedbackSubmissionSuccess(shopperId, trackingData);
  } catch (err) {
    trackCustomerFeedbackSubmissionFailed(
      shopperId,
      err as object,
      trackingData
    );
    throw err;
  }
};

/**
 * Submit the customer's feedback to a design review request group on an anonymous order
 */
type SubmitCustomerFeedbackGroupForAnonymousOrderParams = {
  orderNumber: string;
  groupId: string;
  email: string;
  feedbackSubmission: FeedbackSubmission;
  environment: string;
  trackingData: object;
};

export const submitCustomerFeedbackGroupForAnonymousOrder = async ({
  orderNumber,
  groupId,
  email,
  feedbackSubmission,
  environment,
  trackingData,
}: SubmitCustomerFeedbackGroupForAnonymousOrderParams) => {
  const baseUrl = getConfigForEnvironment(environment);
  const url = `${baseUrl}/api/v1/email/${email}/orders/${orderNumber}/groupId/${groupId}/submitFeedback`;

  const metricsData = {
    email,
    trackingData,
  };
  trackAnonymousCustomerFeedbackSubmissionAttempted(metricsData);

  try {
    await dispatchPostRequest({ url, body: feedbackSubmission });
    trackAnonymousCustomerFeedbackSubmissionSuccess(metricsData);
  } catch (err) {
    trackAnonymousCustomerFeedbackSubmissionFailed(err as object, metricsData);
    throw err;
  }
};
